/**
 * Build version management.
 * - Detects stale builds and triggers a single hard reload.
 * - Provides a manual "force refresh" helper.
 * - Exposes the current build ID for traceability.
 */

declare const __BUILD_ID__: string;
declare const __BUILD_TIME__: string;

const STORAGE_KEY = "perform_build_id";
const RELOAD_GUARD_KEY = "perform_reload_guard";

export const BUILD_ID: string =
  typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

export const BUILD_TIME: string =
  typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : new Date().toISOString();

/**
 * Check if stored build differs from current build.
 * If yes, perform a single hard reload (guarded against loops).
 * Call this once at app startup.
 */
export function checkBuildVersion(): void {
  if (BUILD_ID === "dev") return; // skip in dev

  const stored = localStorage.getItem(STORAGE_KEY);
  const guard = sessionStorage.getItem(RELOAD_GUARD_KEY);

  if (stored && stored !== BUILD_ID && !guard) {
    // Mark guard BEFORE reload to prevent loop
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    localStorage.setItem(STORAGE_KEY, BUILD_ID);

    // Flag that this was a hard reload (for restore toast)
    try { sessionStorage.setItem("perform_was_hard_reload", "1"); } catch {}

    // Clear SW caches
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Unregister service workers then reload
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        Promise.all(regs.map((r) => r.unregister())).then(() => {
          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }
    return;
  }

  // Store current build
  localStorage.setItem(STORAGE_KEY, BUILD_ID);
  // Clear guard on successful load
  sessionStorage.removeItem(RELOAD_GUARD_KEY);
}

/**
 * Manual hard refresh: clears caches, unregisters SW, reloads.
 */
export async function forceRefresh(): Promise<void> {
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // Best effort
  }
  localStorage.setItem(STORAGE_KEY, BUILD_ID);
  sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  try { sessionStorage.setItem("perform_was_hard_reload", "1"); } catch {}
  // Use href assignment as primary method (works in iframes/PWA)
  // fallback to reload() if same-origin restrictions apply
  try {
    window.location.href = window.location.pathname + window.location.search;
  } catch {
    window.location.reload();
  }
}
